import { Button, Typography } from '@breetometer/components';
import { isValidClass } from 'BreetHelpers';
import { useComponentVisibilityApi } from 'BreetHooks';
import { type RefObject, useCallback } from 'react';

import { DropdownWrapper } from '../Dropdown/components/Wrapper';
import type { IDropdownButton, IDropdownOption } from './DropdownButton.helpers';

const DropdownButton = ({
	options,
	wrapperClassName,
	dropdownWrapperClassName,
	itemClassName,
	itemButtonClassName,
	listClassName,
	dropDownComponent,
	placement = 'right',
	leadingIcon,
	trailingIcon,
	switchLeadingMobileIcon,
	switchTrailingMobileIcon,
	onChange,
	switchMobileWidth = 768,
	...rest
}: IDropdownButton) => {
	const controlRefClassName = `dropdownbutton_controlwrapper ${placement}${isValidClass(dropdownWrapperClassName)}`;

	const { isMobile } = useComponentVisibilityApi(switchMobileWidth);

	const showLeadingIconMobile = !!switchLeadingMobileIcon && isMobile;
	const showTrailingIconMobile = !!switchTrailingMobileIcon && isMobile;
	const showLeadingIcon = showLeadingIconMobile || !!leadingIcon;
	const showTrailingIcon = showTrailingIconMobile || !!trailingIcon;

	const handleItemClick = useCallback(
		(item: IDropdownOption, callBack: () => void) => () => {
			onChange?.(item);
			callBack();
		},
		[onChange]
	);

	return (
		<div className='dropdownbutton'>
			<DropdownWrapper>
				{({ buttonRef, controlRef, dropdownRef, dropdownStyles, handleVisibility, visible }) => {
					const component = typeof dropDownComponent === 'function' ? dropDownComponent(handleVisibility) : dropDownComponent;

					return (
						<div className={`dropdownbutton_wrapper${isValidClass(wrapperClassName)}`}>
							<Button
								{...rest}
								ref={buttonRef}
								onClick={handleVisibility}
								label={showLeadingIconMobile || showTrailingIconMobile ? '' : rest.label}
								leadingIcon={showLeadingIcon ? leadingIcon : undefined}
								trailingIcon={showTrailingIcon ? trailingIcon : undefined}
								className='btn_control'
							/>
							{visible && (
								<div
									ref={controlRef as RefObject<HTMLDivElement>}
									className={controlRefClassName}
								>
									<div
										ref={dropdownRef}
										style={dropdownStyles}
										className={`dropdownbutton_dropdownwrapper${isValidClass(listClassName)}`}
									>
										{component ?? (
											<Typography
												as='ul'
												className={`dropdownbutton_listClassName${isValidClass(listClassName)}`}
											>
												{options?.map((optionItem) => {
													if ('label' in optionItem)
														return (
															<Typography
																as='li'
																key={optionItem.label}
																className={`dropdownbutton_itemClassName${isValidClass(itemClassName)}`}
															>
																<Button
																	variant='native'
																	onClick={handleItemClick(optionItem, handleVisibility)}
																	className={`dropdownbutton_itemButtonClassName${isValidClass(itemButtonClassName)}`}
																>
																	{optionItem.icon}
																	{optionItem.label}
																</Button>
															</Typography>
														);
													return (
														<Typography
															as='li'
															key={`li_${optionItem.key}_`}
															className={`dropdownbutton_itemClassName${isValidClass(itemClassName)}`}
														>
															{optionItem}
														</Typography>
													);
												})}
											</Typography>
										)}
									</div>
								</div>
							)}
						</div>
					);
				}}
			</DropdownWrapper>
		</div>
	);
};

export default DropdownButton;
